/* ============================================================
   Somada Custom theme — theme.js
   Phase 1: AJAX add-to-cart + live cart-drawer refresh (Section
   Rendering API), PDP gallery, quantity steppers, sticky bars,
   announcement rotation.

   Architecture rules:
   - Clicks/submits are DELEGATED on document, so nothing needs
     re-binding after the drawer re-renders or the theme editor
     re-injects a section's HTML.
   - Everything degrades gracefully with JS off: cart openers are
     real links to /cart, drawer qty controls are real /cart/change
     links, the product form posts natively to /cart/add.
   - IntersectionObserver-based reveals re-init on
     shopify:section:load for a sane editor experience.
   ============================================================ */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ============================================================
     Cart drawer — open/close, focus containment, live refresh
     ============================================================ */
  function drawer() { return document.getElementById('cartDrawer'); }
  function scrim() { return document.getElementById('cartScrim'); }
  function drawerOpen() { var d = drawer(); return !!(d && d.classList.contains('on')); }

  var lastFocus = null;
  var lbLastFocus = null;
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function setBackgroundInert(on) {
    var d = drawer(), s = scrim();
    Array.prototype.forEach.call(document.querySelectorAll('body > *'), function (el) {
      if (el === d || el === s || el.tagName === 'SCRIPT') return;
      // As a section, the drawer + scrim sit inside a #shopify-section-* wrapper
      // that IS the body-level element here. Skipping only the exact nodes would
      // inert that wrapper and freeze the drawer inside it — so skip any top-level
      // element that CONTAINS the drawer or scrim.
      if ((d && el.contains(d)) || (s && el.contains(s))) return;
      if (on) { el.setAttribute('inert', ''); el.setAttribute('aria-hidden', 'true'); }
      else { el.removeAttribute('inert'); el.removeAttribute('aria-hidden'); }
    });
  }

  function openDrawer() {
    var d = drawer(), s = scrim();
    if (!d || !s) return;
    lastFocus = document.activeElement;
    setBackgroundInert(true);
    document.documentElement.classList.add('drawer-open');
    d.classList.add('on');
    s.classList.add('on');
    d.setAttribute('aria-hidden', 'false');
    var closeBtn = d.querySelector('.cd-close');
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    var d = drawer(), s = scrim();
    if (!d || !s) return;
    d.classList.remove('on');
    s.classList.remove('on');
    d.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('drawer-open');
    setBackgroundInert(false);
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  /* Swap the drawer's contents for a freshly rendered copy (Section Rendering API). */
  function applyDrawerSection(sections) {
    var d = drawer();
    if (!d || !sections || !sections['cart-drawer']) return;
    var holder = document.createElement('div');
    holder.innerHTML = sections['cart-drawer'];
    var fresh = holder.querySelector('#cartDrawer');
    if (!fresh) return;
    /* Hold the scroll position of the items region across the swap, but ONLY when the
       drawer was already open. A fresh add-to-cart opens the drawer and must start at
       the top, on the product just added; a tier or cross-sell tap happens mid-scroll
       and should not fling the shopper back up. */
    var prevBody = d.querySelector('.cd-body');
    var keepScroll = (drawerOpen() && prevBody) ? prevBody.scrollTop : 0;
    d.innerHTML = fresh.innerHTML;
    if (keepScroll) {
      var newBody = d.querySelector('.cd-body');
      if (newBody) newBody.scrollTop = keepScroll;
    }
    if (drawerOpen() && (document.activeElement === document.body || !d.contains(document.activeElement))) {
      var closeBtn = d.querySelector('.cd-close');
      if (closeBtn) closeBtn.focus();
    }
  }

  function updateCartCount(n) {
    if (typeof n !== 'number') return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = n; });
  }

  function refreshDrawerFromServer() {
    return fetch(root + '?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (sections) { applyDrawerSection(sections); });
  }

  /* Drawer qty +/- : the links are real /cart/change URLs — read them, post via AJAX. */
  function cartChangeFromLink(a) {
    var url;
    try { url = new URL(a.href, window.location.origin); } catch (e) { window.location.href = a.href; return; }
    var body = {
      line: parseInt(url.searchParams.get('line'), 10),
      quantity: parseInt(url.searchParams.get('quantity'), 10),
      sections: 'cart-drawer'
    };
    fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
      .then(function (cart) {
        applyDrawerSection(cart.sections);
        updateCartCount(cart.item_count);
      })
      .catch(function () { window.location.href = a.href; });
  }

  /* AJAX add-to-cart — any form posting to /cart/add (buy box + sticky bar via form=).
     CAPTURE phase (third arg true), deliberately: Shopify's storefront analytics binds its own
     submit listener directly to the product form and records an add-to-cart unless the event
     is already cancelled. A bubble-phase listener on document ran after it, so one tap was
     counted twice (add-form + add-fetch). Capture runs first, preventDefault() lands first,
     and Shopify's own check suppresses the duplicate. Shopify ticket #70060933, fixed 2026-09-05. */
  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    if (!window.fetch || !window.FormData) return; // native submit fallback
    ev.preventDefault();

    var err = form.querySelector('[data-atc-error]');
    if (err) err.hidden = true;
    var submitters = form.querySelectorAll('[type="submit"]');
    submitters.forEach(function (b) { b.disabled = true; });

    var fd = new FormData(form);
    fd.append('sections', 'cart-drawer');

    fetch(root + 'cart/add.js', { method: 'POST', body: fd })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        if (!res.ok) {
          var msg = (res.d && (res.d.description || res.d.message)) || '';
          throw new Error(msg);
        }
        applyDrawerSection(res.d.sections);
        return fetch(root + 'cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) { updateCartCount(cart.item_count); });
      })
      .then(function () {
        if (drawer()) openDrawer();
        else window.location.href = root + 'cart';
      })
      .catch(function (e) {
        if (err) {
          err.textContent = (e && e.message) || err.getAttribute('data-fallback') || 'Error';
          err.hidden = false;
        } else {
          form.submit(); // last-resort native fallback
        }
      })
      .finally(function () {
        submitters.forEach(function (b) { b.disabled = false; });
      });
  }, true);

  /* Add a single variant by id (cart-drawer cross-sell) — AJAX, then re-render the
     drawer so the just-added product drops off the suggestions. */
  function addVariant(id, btn, qty) {
    if (!id || !window.fetch) { window.location.href = root + 'cart'; return; }
    var n = parseInt(qty, 10);
    if (!n || n < 1) n = 1;
    if (btn) btn.disabled = true;
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(id, 10), quantity: n, sections: 'cart-drawer' })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        applyDrawerSection(res.d.sections);
        return fetch(root + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) { updateCartCount(cart.item_count); });
      })
      .then(function () { if (!drawerOpen()) openDrawer(); })
      .catch(function () { if (btn) btn.disabled = false; window.location.href = root + 'cart'; });
  }

  /* ============================================================
     PDP image lightbox v2 — opens to the tapped image, navigates the
     gallery (arrows / swipe / ← →), and zooms centred on the point you
     tap. Markup: sections/main-product.liquid.
     ============================================================ */
  var lbList = [], lbIndex = 0, lbSuppressClick = false;
  var heroSuppressClick = false;   /* a hero swipe just navigated — swallow the tap-zoom that follows */

  function lightbox() { return document.querySelector('[data-lightbox]'); }
  function lightboxOpen() { var b = lightbox(); return !!(b && !b.hidden); }
  function lbStage() { var b = lightbox(); return b && b.querySelector('[data-lb-stage]'); }
  function lbImg() { var b = lightbox(); return b && b.querySelector('[data-lb-img]'); }

  /* The gallery's media at full size, in order — read off the thumb strip;
     falls back to the single main image when there are no thumbs. */
  function buildLbList() {
    var list = [];
    document.querySelectorAll('[data-thumb]').forEach(function (t) {
      list.push({ src: t.getAttribute('data-zoom') || t.getAttribute('data-src'), alt: t.getAttribute('data-alt') || '' });
    });
    if (!list.length) {
      var mi = document.querySelector('[data-main-img]');
      if (mi) list.push({ src: mi.getAttribute('data-zoom-src') || mi.src, alt: mi.alt || '' });
    }
    return list;
  }

  function lbShow(i) {
    var img = lbImg(), stage = lbStage();
    if (!img || !lbList.length) return;
    lbIndex = (i + lbList.length) % lbList.length;
    img.src = lbList[lbIndex].src;
    img.alt = lbList[lbIndex].alt;
    if (stage) { stage.classList.remove('zoomed'); stage.scrollTo(0, 0); }
  }

  function lbNav(dir) { if (lbList.length > 1) lbShow(lbIndex + dir); }

  /* Zoom in centred on (clientX,clientY); a second tap zooms back out. */
  function lbToggleZoom(clientX, clientY) {
    var stage = lbStage(), img = lbImg();
    if (!stage || !img) return;
    if (stage.classList.contains('zoomed')) { stage.classList.remove('zoomed'); stage.scrollTo(0, 0); return; }
    var r = img.getBoundingClientRect();
    var fx = r.width ? (clientX - r.left) / r.width : 0.5;
    var fy = r.height ? (clientY - r.top) / r.height : 0.5;
    fx = Math.min(1, Math.max(0, fx));
    fy = Math.min(1, Math.max(0, fy));
    stage.classList.add('zoomed');
    requestAnimationFrame(function () {
      stage.scrollTo({ left: fx * stage.scrollWidth - stage.clientWidth / 2, top: fy * stage.scrollHeight - stage.clientHeight / 2 });
    });
  }

  function openLightbox(index) {
    var box = lightbox();
    if (!box) return;
    lbList = buildLbList();
    if (!lbList.length) return;
    var prev = box.querySelector('[data-lb-prev]'), next = box.querySelector('[data-lb-next]');
    var multi = lbList.length > 1;
    if (prev) prev.hidden = !multi;
    if (next) next.hidden = !multi;
    lbShow(index || 0);
    box.hidden = false;
    box.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('lb-open');
    lbLastFocus = document.activeElement;
    var closeBtn = box.querySelector('[data-lb-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeLightbox() {
    var box = lightbox();
    if (!box || box.hidden) return;
    box.hidden = true;
    box.setAttribute('aria-hidden', 'true');
    var stage = lbStage();
    if (stage) { stage.classList.remove('zoomed'); stage.scrollTo(0, 0); }
    document.documentElement.classList.remove('lb-open');
    if (lbLastFocus && typeof lbLastFocus.focus === 'function') lbLastFocus.focus();
  }

  /* Swipe: navigate when not zoomed; leave native scroll to pan when zoomed. */
  function initLightbox() {
    var stage = lbStage();
    if (!stage || stage._somadaLbInit) return;
    stage._somadaLbInit = true;
    var start = null;
    stage.addEventListener('touchstart', function (e) {
      start = e.touches.length === 1 ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
    }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (!start) return;
      var t = e.changedTouches[0], dx = t.clientX - start.x, dy = t.clientY - start.y;
      start = null;
      if (stage.classList.contains('zoomed')) return;          /* zoomed = pan, not nav */
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) + 10) {
        lbNav(dx < 0 ? 1 : -1);
        lbSuppressClick = true;                                 /* cancel the tap-to-zoom that follows a swipe */
        setTimeout(function () { lbSuppressClick = false; }, 350);
      }
    }, { passive: true });
  }

  /* ============================================================
     Delegated clicks — drawer openers/closers, qty steppers,
     drawer qty links, PDP gallery thumbs, image lightbox.
     ============================================================ */
  document.addEventListener('click', function (ev) {
    var t = ev.target;

    /* PDP hero slider arrows (mobile) — navigate via the thumb machinery. */
    if (t.closest('[data-hero-prev]')) { ev.preventDefault(); heroNav(-1); return; }
    if (t.closest('[data-hero-next]')) { ev.preventDefault(); heroNav(1); return; }

    /* Lightbox first: open it (to the image on screen), navigate, zoom, or dismiss. */
    var zoomOpen = t.closest('[data-zoom-open]');
    if (zoomOpen) {
      if (heroSuppressClick) { heroSuppressClick = false; return; }   /* a swipe just handled this */
      ev.preventDefault();
      var thumbs = document.querySelectorAll('[data-thumb]'), idx = 0;
      for (var k = 0; k < thumbs.length; k++) { if (thumbs[k].classList.contains('on')) { idx = k; break; } }
      openLightbox(idx);
      return;
    }
    if (lightboxOpen()) {
      var box = lightbox();
      if (t.closest('[data-lb-close]')) { closeLightbox(); return; }
      if (t.closest('[data-lb-prev]')) { lbNav(-1); return; }
      if (t.closest('[data-lb-next]')) { lbNav(1); return; }
      if (t.closest('[data-lb-img]')) {
        if (lbSuppressClick) { lbSuppressClick = false; return; }   /* a swipe just handled this */
        lbToggleZoom(ev.clientX, ev.clientY);
        return;
      }
      if (box.contains(t)) { closeLightbox(); return; }   /* backdrop / stage padding */
    }

    var crossAdd = t.closest('[data-cross-add]');
    if (crossAdd) { ev.preventDefault(); addVariant(crossAdd.getAttribute('data-cross-add'), crossAdd); return; }

    /* Bundle-ladder top-up: adds exactly the shortfall to the next tier in one tap.
       The drawer re-renders from the response, so the ladder restates itself. */
    var tierAdd = t.closest('[data-tier-add]');
    if (tierAdd) {
      ev.preventDefault();
      addVariant(tierAdd.getAttribute('data-tier-add'), tierAdd, tierAdd.getAttribute('data-tier-qty'));
      return;
    }

    var opener = t.closest('[data-cart-open]');
    if (opener) { ev.preventDefault(); openDrawer(); return; }

    if (t.closest('.cd-close') || t.closest('.cd-keep')) { closeDrawer(); return; }
    if (t.closest('#cartScrim')) { closeDrawer(); return; }

    var qLink = t.closest('[data-qty-down],[data-qty-up]');
    if (qLink && drawer() && drawer().contains(qLink)) { ev.preventDefault(); cartChangeFromLink(qLink); return; }

    /* PDP multibuy tiles: set the buy-form quantity AND repaint the visible price story
       (F-15 — the headline price, per-day line and sticky bar must follow the selection;
       before this, picking a tier changed nothing the buyer could see). The tiles are a
       choice set, not a separate cart path — the real discount still comes from Shopify
       at checkout; the repaint values are Liquid-computed data attributes on each tile,
       derived from the same fixed discount amounts, so page and checkout agree. */
    var preset = t.closest('[data-qty-preset]');
    if (preset) {
      ev.preventDefault();
      var wrap = preset.closest('.buy');
      var qtyInput = wrap && wrap.querySelector('.qty input[name="quantity"]');
      if (qtyInput) {
        qtyInput.value = preset.getAttribute('data-qty-preset');
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      var group = preset.parentNode;
      var tiles = group ? group.querySelectorAll('[data-qty-preset]') : [];
      for (var ti = 0; ti < tiles.length; ti++) {
        var on = tiles[ti] === preset;
        tiles[ti].classList.toggle('is-on', on);
        tiles[ti].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      repaintTierPrice(preset);
      return;
    }

    var step = t.closest('[data-step]');
    if (step) {
      var box = step.closest('.qty');
      var input = box && box.querySelector('input');
      if (input) {
        var v = parseInt(input.value, 10) || 1;
        var min = parseInt(input.min, 10) || 1;
        var max = parseInt(input.max, 10) || 99;
        input.value = step.getAttribute('data-step') === 'up' ? Math.min(max, v + 1) : Math.max(min, v - 1);
      }
      return;
    }

    var thumb = t.closest('[data-thumb]');
    if (thumb) {
      var gallery = thumb.closest('.gallery');
      var main = gallery && gallery.querySelector('[data-main-img]');
      if (main) {
        if (thumb.dataset.src) main.src = thumb.dataset.src;
        if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
        if (thumb.dataset.zoom) main.setAttribute('data-zoom-src', thumb.dataset.zoom);
        if (thumb.dataset.alt !== undefined) main.alt = thumb.dataset.alt;
        var thumbList = gallery.querySelectorAll('[data-thumb]');
        var thumbIdx = 0;
        thumbList.forEach(function (b, i) {
          b.classList.toggle('on', b === thumb);
          if (b === thumb) thumbIdx = i;
        });
        updateHeroDots(thumbIdx);
        centreThumb(thumb);
      }
      return;
    }
  });

  /* Mobile hero slider (2026-08-28): the hero image navigates the gallery directly —
     arrows + swipe + dots — because the thumb strip is hidden under 960px. Navigation
     goes THROUGH the (hidden) thumb buttons, so image swap, thumb state and the
     lightbox's index derivation all stay consistent with zero extra state. */
  function heroNav(dir) {
    var thumbs = document.querySelectorAll('[data-thumb]');
    if (thumbs.length < 2) return;
    var idx = 0;
    for (var i = 0; i < thumbs.length; i++) {
      if (thumbs[i].classList.contains('on')) { idx = i; break; }
    }
    thumbs[(idx + dir + thumbs.length) % thumbs.length].click();
  }

  function updateHeroDots(idx) {
    var dots = document.querySelectorAll('[data-hero-dots] span');
    dots.forEach(function (d, i) { d.classList.toggle('on', i === idx); });
  }

  /* Swipe on the hero: navigate left/right; the tap-to-zoom that a touch also fires is
     suppressed for a beat after a swipe (same idiom as the lightbox). */
  function initHeroSwipe() {
    var hero = document.querySelector('.gallery .main-img');
    if (!hero || hero._somadaSwipe) return;
    hero._somadaSwipe = true;
    var start = null;
    hero.addEventListener('touchstart', function (e) {
      start = e.touches.length === 1 ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
    }, { passive: true });
    hero.addEventListener('touchend', function (e) {
      if (!start) return;
      var t = e.changedTouches[0], dx = t.clientX - start.x, dy = t.clientY - start.y;
      start = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) + 10) {
        heroNav(dx < 0 ? 1 : -1);
        heroSuppressClick = true;
        setTimeout(function () { heroSuppressClick = false; }, 350);
      }
    }, { passive: true });
  }

  /* F-15: swap the headline price, per-day line and sticky-bar line to the selected
     tier's figures (Liquid-computed data attributes on the tile), and restore the
     originals when tile 1 is re-selected. Originals are cached on first use. */
  function setPerLine(el, line) {
    el.innerHTML = '';
    line.split('·').forEach(function (part) {
      var s = document.createElement('span');
      s.textContent = part.trim();
      el.appendChild(s);
    });
  }
  function repaintTierPrice(preset) {
    var section = preset.closest('.pdp');
    if (!section) return;
    var priceEl = section.querySelector('.buy .price');
    var perEl = section.querySelector('.buy .per');
    var stickyEl = section.querySelector('.sticky-atc .s-price');
    if (priceEl && priceEl.dataset.base === undefined) priceEl.dataset.base = priceEl.textContent;
    if (perEl && perEl.dataset.base === undefined) perEl.dataset.base = perEl.innerHTML;
    if (stickyEl && stickyEl.dataset.base === undefined) stickyEl.dataset.base = stickyEl.textContent;
    var tierPrice = preset.getAttribute('data-tier-price');
    if (tierPrice) {
      if (priceEl) priceEl.textContent = tierPrice;
      var tierPer = preset.getAttribute('data-tier-per');
      if (perEl && tierPer) setPerLine(perEl, tierPer);
      var tierSticky = preset.getAttribute('data-tier-sticky');
      if (stickyEl && tierSticky) stickyEl.textContent = tierSticky;
    } else {
      if (priceEl && priceEl.dataset.base !== undefined) priceEl.textContent = priceEl.dataset.base;
      if (perEl && perEl.dataset.base !== undefined) perEl.innerHTML = perEl.dataset.base;
      if (stickyEl && stickyEl.dataset.base !== undefined) stickyEl.textContent = stickyEl.dataset.base;
    }
  }

  /* Bring the chosen thumb to the middle of the strip, so clicking through the
     gallery carries the strip with it instead of making you drag it by hand.
     Measured off rects rather than offsetLeft: .thumbs isn't a positioned
     element, so offsetLeft would resolve against the wrong ancestor. The browser
     clamps scrollLeft at both ends, so the first/last thumbs simply sit where
     they are rather than leaving a gap. */
  function centreThumb(thumb) {
    var strip = thumb.parentNode;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;
    var s = strip.getBoundingClientRect();
    var t = thumb.getBoundingClientRect();
    var delta = (t.left - s.left) - ((s.width - t.width) / 2);
    strip.scrollTo({ left: strip.scrollLeft + delta });   /* smoothness comes from CSS scroll-behavior */
  }

  /* Keyboard: Esc closes the lightbox; ← / → step through the gallery. */
  document.addEventListener('keydown', function (ev) {
    if (!lightboxOpen()) return;
    if (ev.key === 'Escape') closeLightbox();
    else if (ev.key === 'ArrowLeft') lbNav(-1);
    else if (ev.key === 'ArrowRight') lbNav(1);
  });

  /* Keyboard: ESC closes the drawer; Tab is contained while it's open. */
  document.addEventListener('keydown', function (ev) {
    if (!drawerOpen()) return;
    var d = drawer();
    if (ev.key === 'Escape') { closeDrawer(); return; }
    if (ev.key !== 'Tab') return;
    var f = d.querySelectorAll(FOCUSABLE);
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
    else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
  });

  /* ============================================================
     Announcement bar — rotate messages (blocks) when enabled
     ============================================================ */
  function initAnnounce(scope) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    (scope || document).querySelectorAll('.announce[data-rotate]').forEach(function (bar) {
      if (bar._somadaTimer) { clearInterval(bar._somadaTimer); bar._somadaTimer = null; }
      var items = bar.querySelectorAll('.announce-item');
      if (items.length < 2) return;
      var interval = (parseInt(bar.dataset.interval, 10) || 5) * 1000;
      var i = 0;
      function advance() {
        if (!bar.isConnected) { clearInterval(bar._somadaTimer); bar._somadaTimer = null; return; }
        items[i].classList.remove('is-active');
        items[i].setAttribute('aria-hidden', 'true');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
        items[i].setAttribute('aria-hidden', 'false');
      }
      function start() { if (!bar._somadaTimer) bar._somadaTimer = setInterval(advance, interval); }
      function stop() { if (bar._somadaTimer) { clearInterval(bar._somadaTimer); bar._somadaTimer = null; } }
      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', start);
      start();
    });
  }

  /* ============================================================
     Sticky reveal bars — home quick-shop + PDP sticky ATC.
     Both appear once their anchor scrolls out of view.
     ============================================================ */
  function observeReveal(anchor, bar) {
    if (!anchor || !bar || !('IntersectionObserver' in window)) return;
    if (bar._somadaIO) bar._somadaIO.disconnect();
    bar._somadaIO = new IntersectionObserver(function (entries) {
      var gone = !entries[0].isIntersecting;
      bar.classList.toggle('on', gone);
      bar.setAttribute('aria-hidden', gone ? 'false' : 'true');
    }, { rootMargin: '-60px 0px 0px 0px' });
    bar._somadaIO.observe(anchor);
  }

  function initStickyBars(scope) {
    var doc = scope || document;
    var shopBar = document.getElementById('stickyShop');
    if (shopBar) observeReveal(document.querySelector('[data-sticky-anchor]'), shopBar);
    doc.querySelectorAll('[data-sticky-atc]').forEach(function (bar) {
      var section = bar.closest('[data-atc-anchor]') || document.querySelector('[data-atc-anchor]');
      /* F-01: watch the buy FORM, not the whole section — the bar shows whenever the
         form is off-screen (including at load, before the visitor reaches it), instead
         of only after the entire buy section has scrolled past (~28% depth). */
      var watch = (section && section.querySelector('[data-atc-watch]')) || section;
      observeReveal(watch, bar);
    });
  }

  /* ============================================================
     info-pop — small "i" explainers (snippets/info-pop.liquid)

     Delegated, so it survives section re-renders with no re-init.
     The panel is position:fixed and tethered here rather than in CSS:
     absolute positioning would let any overflow:hidden ancestor (the buy
     box, a card) clip it, which is the exact thing we're avoiding.
     Under 560px the CSS pins it as a bottom card, so we skip placement.
     ============================================================ */
  var POP_GAP = 8, POP_EDGE = 12;
  var openPopBtn = null;

  function popIsPhone() {
    return !!(window.matchMedia && window.matchMedia('(max-width:560px)').matches);
  }

  function popPanelFor(btn) {
    return btn ? document.getElementById(btn.getAttribute('aria-controls')) : null;
  }

  function placePop(btn, panel) {
    if (popIsPhone()) return true;  /* CSS owns the phone layout */
    var b = btn.getBoundingClientRect();
    if (b.bottom < 0 || b.top > window.innerHeight) return false;  /* button scrolled away */
    panel.style.left = '0px';
    panel.style.top = '0px';
    var p = panel.getBoundingClientRect();
    var left = b.left + (b.width / 2) - (p.width / 2);
    left = Math.max(POP_EDGE, Math.min(left, window.innerWidth - p.width - POP_EDGE));
    var top = b.bottom + POP_GAP;
    if (top + p.height > window.innerHeight - POP_EDGE) {
      var above = b.top - p.height - POP_GAP;      /* flip above rather than run off-screen */
      top = above >= POP_EDGE ? above : Math.max(POP_EDGE, window.innerHeight - p.height - POP_EDGE);
    }
    panel.style.left = Math.round(left) + 'px';
    panel.style.top = Math.round(top) + 'px';
    return true;
  }

  function closePop() {
    if (!openPopBtn) return;
    var panel = popPanelFor(openPopBtn);
    openPopBtn.setAttribute('aria-expanded', 'false');
    if (panel) { panel.hidden = true; panel.style.left = ''; panel.style.top = ''; }
    openPopBtn = null;
  }

  function openPop(btn) {
    closePop();                       /* only ever one open */
    var panel = popPanelFor(btn);
    if (!panel) return;
    panel.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    openPopBtn = btn;
    placePop(btn, panel);
  }

  document.addEventListener('click', function (ev) {
    if (!ev.target.closest) return;
    var btn = ev.target.closest('[data-info-pop-btn]');
    if (btn) {
      if (openPopBtn === btn) closePop(); else openPop(btn);
      return;
    }
    if (openPopBtn && !ev.target.closest('[data-info-pop-panel]')) closePop();
  });

  document.addEventListener('keydown', function (ev) {
    if (ev.key !== 'Escape' || !openPopBtn) return;
    var btn = openPopBtn;
    closePop();
    btn.focus();                      /* Escape returns you where you were */
  });

  function repositionPop() {
    if (!openPopBtn) return;
    var panel = popPanelFor(openPopBtn);
    if (!panel || panel.hidden || !openPopBtn.isConnected) { closePop(); return; }
    if (!placePop(openPopBtn, panel)) closePop();
  }
  window.addEventListener('scroll', repositionPop, { passive: true });
  window.addEventListener('resize', repositionPop, { passive: true });

  /* ============================================================
     Boot + theme-editor re-init
     ============================================================ */
  initAnnounce(document);
  initStickyBars(document);
  initLightbox();
  initHeroSwipe();

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      initAnnounce(e.target);
      initStickyBars(e.target);
      initLightbox();
      initHeroSwipe();
    });
  }
})();
